import type { FieldType, FieldMeta } from '../meta/types';
import type { FilterOp } from '../api/client';

// The field-type LIBRARY: every FieldType with its default properties in one place — the blank
// value a create form starts from, how a column aligns, which filter operators fit, whether the
// value is text-like (so '' means "no value") and how it groups in forms. Editors and cell
// renderers live in registry.tsx (keyed by the same FieldType); zodFromMeta and defaultsFromMeta
// read the defaults here instead of re-listing the types.

export interface FieldTypeDef {
  /** Blank value for a create form (undefined → the field starts absent). */
  blank: unknown;
  /** Text-like: '' is "no value", trimmed, min(1) when required. */
  textual: boolean;
  /** Numeric (integer or decimal) — right-aligned, numeric filters. */
  numeric: boolean;
  /** Default column alignment. */
  align: 'left' | 'right' | 'center';
  /** Filter operators offered by default. */
  ops: FilterOp[];
  /** Full-width in a two-column form by default. */
  wide?: boolean;
}

const TEXT_OPS: FilterOp[] = ['ilike', 'eq', 'ne', 'is_null'];
const NUM_OPS: FilterOp[] = ['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'between', 'is_null'];
const DATE_OPS: FilterOp[] = ['eq', 'gt', 'gte', 'lt', 'lte', 'between', 'is_null'];
const ENUM_OPS: FilterOp[] = ['eq', 'ne', 'in', 'nin', 'is_null'];
const BOOL_OPS: FilterOp[] = ['eq'];
const REL_OPS: FilterOp[] = ['eq', 'ne', 'in', 'is_null'];

const text: FieldTypeDef = { blank: '', textual: true, numeric: false, align: 'left', ops: TEXT_OPS };
const num: FieldTypeDef = { blank: null, textual: false, numeric: true, align: 'right', ops: NUM_OPS };

export const FIELD_TYPES: Record<FieldType, FieldTypeDef> = {
  text,
  email: text,
  phone: text,
  url: text,
  color: { ...text, ops: ['eq', 'is_null'] },
  textarea: { ...text, wide: true },
  richtext: { ...text, wide: true },
  number: num,
  integer: num,
  money: num,
  duration: num,
  boolean: { blank: false, textual: false, numeric: false, align: 'center', ops: BOOL_OPS },
  date: { blank: null, textual: false, numeric: false, align: 'left', ops: DATE_OPS },
  datetime: { blank: null, textual: false, numeric: false, align: 'left', ops: DATE_OPS },
  enum: { blank: null, textual: false, numeric: false, align: 'left', ops: ENUM_OPS },
  uuid: { blank: null, textual: false, numeric: false, align: 'left', ops: ['eq', 'is_null'] },
  relation: { blank: null, textual: false, numeric: false, align: 'left', ops: REL_OPS },
  json: { blank: null, textual: false, numeric: false, align: 'left', ops: ['is_null'], wide: true },
  tags: { blank: [], textual: false, numeric: false, align: 'left', ops: ['contains', 'is_null'], wide: true },
};

export function typeDef(f: Pick<FieldMeta, 'type'>): FieldTypeDef {
  return FIELD_TYPES[f.type] ?? text;
}

/** The blank a create form starts a field from: FieldMeta.default, a shape's empty value, else the type's blank. */
export function blankFor(f: Pick<FieldMeta, 'type' | 'default' | 'shape'>): unknown {
  if (f.default !== undefined) return f.default;
  if (f.type === 'json' && f.shape) {
    if (f.shape.kind === 'object') return {};
    if (f.shape.kind === 'list') return [];
    if (f.shape.kind === 'map') return {};
  }
  return typeDef(f).blank;
}
